import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { Form, Input, TextArea, ChipInput, Tabs } from '@engloba-tech/englobity';
import PeopleIcon from '@material-ui/icons/People';
import { NAME_SPACE } from 'i18n';
import {
  isSkeletonLoading,
  LocaleDatePicker,
  enumHelper,
  READER_REQUEST_STATUSES,
  READER_REQUEST_TYPES,
  SelectInput
} from 'shared';
import { useReaderRequestForm } from './useReaderRequestForm';
import { useFormReaderRequestStyles } from './formReaderRequest.styles';
import { ViewOperators } from '../ViewOperators';

export function FormReaderRequest({ formRef, onSubmit, readerRequest, errors }) {
  const { t } = useTranslation(NAME_SPACE.READER_REQUESTS);
  const classes = useFormReaderRequestStyles();
  const { readerInputs } = useReaderRequestForm(readerRequest);

  const requestStatusesElements = useMemo(() => {
    return enumHelper.enumToCombo(READER_REQUEST_STATUSES, t, 'statuses');
  }, [t]);

  const requestTypesElements = useMemo(() => {
    if (!readerInputs?.requestType) return [];
    var requestTypes = enumHelper.enumToCombo(READER_REQUEST_TYPES, t, 'types');
    return requestTypes.filter(r => r.value & readerInputs.requestType).map(r => r.name);
  }, [t, readerInputs]);

  function handleSubmit() {
    onSubmit(readerInputs);
  }

  const hasObservations = (readerInputs?.requestType & READER_REQUEST_TYPES.replaceDefectiveTags) > 0;

  const renderTabs = useMemo(() => {
    let tabs = [
      {
        name: t('operators.name'),
        icon: <PeopleIcon />,
        children: <ViewOperators operators={readerInputs?.operators ?? []} />
      }
    ];

    return tabs;
  }, [t, readerInputs]);

  return (
    <Form
      className={classes.formReaderRequest}
      errors={errors}
      elementRef={formRef}
      onSubmit={handleSubmit}
      autoComplete="off"
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6}>
          <ChipInput
            isLoading={isSkeletonLoading(readerInputs?.requestType)}
            label={t('properties.requestType')}
            value={requestTypesElements || []}
            className={classes.chip}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3} item>
          <Input
            isLoading={isSkeletonLoading(readerInputs.requestNumber)}
            name={t('properties.requestNumber')}
            label={t('properties.requestNumber')}
            variant="outlined"
            value={readerInputs.requestNumber || ''}
            inputProps={{ 'aria-label': t('properties.requestNumber') }}
            readOnly
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SelectInput
            name={t('properties.requestStatus')}
            label={t('properties.requestStatus')}
            elements={requestStatusesElements}
            value={readerInputs.requestStatus}
            inputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid xs={12} sm={12} md={6} item>
          <Input
            isLoading={isSkeletonLoading(readerInputs.product)}
            name={t('properties.product')}
            label={t('properties.product')}
            variant="outlined"
            value={readerInputs.product || ''}
            inputProps={{ 'aria-label': t('properties.product') }}
            readOnly
          />
        </Grid>
        <Grid xs={12} sm={6} md={3} item>
          <LocaleDatePicker
            icon={null}
            isLoading={isSkeletonLoading(readerInputs.requestDate)}
            name={t('properties.requestDate')}
            label={t('properties.requestDate')}
            value={readerInputs.requestDate || ''}
            readOnly
            inputProps={{ 'aria-label': t('properties.requestDate') }}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3} item>
          <LocaleDatePicker
            icon={null}
            isLoading={isSkeletonLoading(readerInputs.receptionDate)}
            name={t('properties.receptionDate')}
            label={t('properties.receptionDate')}
            value={readerInputs.receptionDate || ''}
            readOnly
            inputProps={{ 'aria-label': t('properties.receptionDate') }}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3} item>
          <Input
            isLoading={isSkeletonLoading(readerInputs.reader)}
            name={t('properties.reader')}
            label={t('properties.reader')}
            variant="outlined"
            value={readerInputs.reader || ''}
            inputProps={{ 'aria-label': t('properties.reader') }}
            readOnly
          />
        </Grid>
        <Grid xs={12} sm={6} md={3} item>
          <Input
            isLoading={isSkeletonLoading(readerInputs.readingStartDate)}
            name={t('properties.readingStartDate')}
            label={t('properties.readingStartDate')}
            value={readerInputs.formattedReadingStartDate || ''}
            readOnly
            inputProps={{ 'aria-label': t('properties.readingStartDate') }}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3} item>
          <Input
            isLoading={isSkeletonLoading(readerInputs.readingEndDate)}
            name={t('properties.readingEndDate')}
            label={t('properties.readingEndDate')}
            value={readerInputs.formattedReadingEndDate || ''}
            readOnly
            inputProps={{ 'aria-label': t('properties.readingEndDate') }}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3} item>
          <Input
            isLoading={isSkeletonLoading(readerInputs.processingTime)}
            name={t('properties.processingTime')}
            label={t('properties.processingTime')}
            variant="outlined"
            type="number"
            value={readerInputs.processingTime ? Number(readerInputs.processingTime / 60000).toFixed(2) : ''}
            inputProps={{ 'aria-label': t('properties.processingTime') }}
            readOnly
          />
        </Grid>
        {(readerInputs?.requestType & READER_REQUEST_TYPES.replaceDefectiveTags) > 0 && (
          <Grid xs={12} sm={6} md={3} item>
            <Input
              isLoading={isSkeletonLoading(readerInputs.ringAmount)}
              name={t('properties.ringAmount')}
              label={t('properties.ringAmount')}
              variant="outlined"
              type="number"
              value={readerInputs.ringAmount || ''}
              inputProps={{ 'aria-label': t('properties.ringAmount') }}
              readOnly
            />
          </Grid>
        )}
        {(readerInputs?.requestType & READER_REQUEST_TYPES.replaceDefectiveTags) <= 0 && (
          <Grid xs={12} sm={6} md={3} item>
            <Input
              isLoading={isSkeletonLoading(readerInputs.boxesAmountToRead)}
              name={t('properties.boxesAmountToRead')}
              label={t('properties.boxesAmountToRead')}
              variant="outlined"
              type="number"
              value={readerInputs.boxesAmountToRead || ''}
              inputProps={{ 'aria-label': t('properties.boxesAmountToRead') }}
              readOnly
            />
          </Grid>
        )}
        {(readerInputs?.requestType & READER_REQUEST_TYPES.setProductionDate) > 0 && (
          <Grid xs={12} sm={6} md={3} item>
            <LocaleDatePicker
              icon={null}
              isLoading={isSkeletonLoading(readerInputs.productionDate)}
              name={t('properties.productionDate')}
              label={t('properties.productionDate')}
              value={readerInputs.productionDate || ''}
              readOnly
              inputProps={{ 'aria-label': t('properties.productionDate') }}
            />
          </Grid>
        )}
        {hasObservations && (
          <Grid item xs={12} md={6}>
            <TextArea
              isLoading={isSkeletonLoading(readerInputs.observations)}
              label={t('properties.observations')}
              value={readerInputs.observations || ''}
              rows={5}
              fullWidth
              readOnly
            />
          </Grid>
        )}
        <Grid item xs={12} md={hasObservations ? 6 : 12}>
          <TextArea
            isLoading={isSkeletonLoading(readerInputs.errors)}
            label={t('properties.errors')}
            value={readerInputs.errors || ''}
            rows={5}
            fullWidth
            readOnly
          />
        </Grid>
      </Grid>
      <Grid container className={classes.containers}>
        <Grid item xs={12}>
          <Tabs classNameContent={classes.tabsMinHeight} tabs={renderTabs} />
        </Grid>
      </Grid>
    </Form>
  );
}

FormReaderRequest.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onSubmit: PropTypes.func,
  errors: PropTypes.shape({
    message: PropTypes.string,
    detail: PropTypes.string
  }),
  readerRequest: PropTypes.shape({
    id: PropTypes.string,
    reader: PropTypes.string,
    product: PropTypes.string,
    requestDate: PropTypes.string,
    receptionDate: PropTypes.string,
    readingStartDate: PropTypes.string,
    readingEndDate: PropTypes.string,
    processingTime: PropTypes.number,
    requestStatus: PropTypes.number,
    requestType: PropTypes.number,
    ringAmount: PropTypes.number,
    boxesAmountToRead: PropTypes.number,
    responseFileUrl: PropTypes.string,
    errors: PropTypes.string,
    observations: PropTypes.string
  }),
  onChangeInput: PropTypes.func
};
